import { Stack } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, CodeField, Select, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { useGetCounterStyles } from 'features/globalSetup/pages/Defaults/RichPlacements/hooks/useGetCounterStyles'
import { useAbility } from 'providers/casl'
import { KNOWLEDGE_BASE_URL } from './constants'
import { Schema } from './schema'
import { useGetWorkspaceDefaults, WorkspaceDefaults } from './useGetWorkspaceDefaults'
import { AppearanceWorkspaceDefaults } from './WorkspaceDefaults/AppearanceWorkspaceDefaults'

export const AppearanceSection = (): React.JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor.appearance',
  })

  const { control, watch } = useFormContext<Schema>()
  const { customizeAppearance } = watch()

  const cannotUpdate: boolean = ability.cannot('update', 'Domain')

  const workspaceDefaults: undefined | WorkspaceDefaults = useGetWorkspaceDefaults()

  const counterStyles = useGetCounterStyles()

  return (
    <Paper data-cy='section-appearance'>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Stack
          direction='row'
          spacing={2}
          sx={{ alignItems: 'flex-start' }}
        >
          <BoxForm>
            <Switch
              control={control}
              disabled={cannotUpdate}
              label={t('customizeAppearance.label')}
              labelPlacement='start'
              name='customizeAppearance'
              tooltip={{ content: t('customizeAppearance.tooltip') }}
            />

            {customizeAppearance && (
              <>
                <Select
                  control={control}
                  disabled={cannotUpdate}
                  label={t('counterStyle.label')}
                  name='counterStyle'
                  options={counterStyles}
                  tooltip={{ content: t('counterStyle.tooltip') }}
                />
                <Switch
                  control={control}
                  disabled={cannotUpdate}
                  label={t('showCloseButton.label')}
                  labelPlacement='start'
                  name='showCloseButton'
                  tooltip={{ content: t('showCloseButton.tooltip') }}
                />
                <Switch
                  control={control}
                  disabled={cannotUpdate}
                  label={t('fullWidthMode.label')}
                  labelPlacement='start'
                  name='fullWidthMode'
                  tooltip={{ content: t('fullWidthMode.tooltip') }}
                />
              </>
            )}
          </BoxForm>

          {customizeAppearance && workspaceDefaults && (
            <AppearanceWorkspaceDefaults workspaceDefaults={workspaceDefaults} />
          )}
        </Stack>

        {customizeAppearance && (
          <BoxForm>
            <CodeField
              control={control}
              disabled={cannotUpdate}
              language='json'
              name='customStyling'
              title={t('customStyling.label')}
              tooltip={{
                content: t('customStyling.tooltip'),
                link: {
                  title: tc('learnMore'),
                  url: KNOWLEDGE_BASE_URL.CUSTOM_STYLING,
                },
              }}
            />
          </BoxForm>
        )}
      </BoxForm>
    </Paper>
  )
}
